import type { ToastVariant } from '@/components/ui/BrandToaster';

/**
 * Flash toast — a toast that must survive a full-page reload.
 *
 * Sonner keeps its queue in client memory, so a toast fired right before a hard
 * navigation (`window.location.href = …`) never paints: the browser unloads the
 * page before React commits it. Logout does exactly that. This stores the intent
 * in sessionStorage before the reload; `BrandToaster` consumes it on mount and
 * fires the real toast on the fresh page.
 *
 * Use it ONLY when a hard reload follows. For soft (CSR) navigation the toaster
 * stays mounted, so a direct `notify.*` call is enough.
 */
const FLASH_TOAST_KEY = 'siiv:flash-toast';

export interface FlashToast {
	variant: ToastVariant;
	title: string;
	description?: string;
}

export function setFlashToast(payload: FlashToast): void {
	if (typeof window === 'undefined') return;
	try {
		window.sessionStorage.setItem(FLASH_TOAST_KEY, JSON.stringify(payload));
	} catch {
		// sessionStorage unavailable (private mode / quota). The toast is a nicety —
		// never block logout because we could not persist it.
	}
}

export function consumeFlashToast(): FlashToast | null {
	if (typeof window === 'undefined') return null;
	try {
		const raw = window.sessionStorage.getItem(FLASH_TOAST_KEY);
		if (!raw) return null;
		window.sessionStorage.removeItem(FLASH_TOAST_KEY);
		return JSON.parse(raw) as FlashToast;
	} catch {
		return null;
	}
}
